import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  auth, 
  googleProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  sendEmailVerification
} from '../lib/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Function to convert Firebase errors to user-friendly messages
const getReadableError = (error) => {
  const errorCode = error.code;
  switch (errorCode) {
    // Auth/Account errors
    case 'auth/email-already-in-use':
      return 'An account with this email already exists';
    case 'auth/invalid-email':
      return 'Please enter a valid email address';
    case 'auth/operation-not-allowed':
      return 'Account creation is currently disabled';
    case 'auth/weak-password':
      return 'Password is too weak. Please use a stronger password';
    
    // Sign in errors
    case 'auth/user-disabled':
      return 'This account has been disabled';
    case 'auth/user-not-found':
      return 'No account found with this email';
    case 'auth/wrong-password':
      return 'Incorrect password';
    case 'auth/invalid-login-credentials':
      return 'Incorrect email or password';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later';
    
    // Reset password errors
    case 'auth/expired-action-code':
      return 'Password reset link has expired';
    case 'auth/invalid-action-code':
      return 'Invalid password reset link';
    
    // General errors
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled';
    case 'auth/internal-error':
      return 'Something went wrong. Please try again';
    
    default:
      return 'An error occurred. Please try again';
  }
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Only set the current user if they've verified their email
      if (user && !user.emailVerified) {
        signOut(auth); // Sign out if email isn't verified
        setCurrentUser(null);
      } else {
        setCurrentUser(user);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Sign up with email and password
  async function signup(email, password) {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(result.user);
      await signOut(auth); // Sign out immediately after signup
      setError('');
      return result.user;
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }

  // Login with email and password
  async function login(email, password) {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (!result.user.emailVerified) {
        await signOut(auth);
        setError('Please verify your email before logging in');
        throw new Error('Email not verified');
      }
      setError('');
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }

  // Resend verification email
  async function resendVerificationEmail() {
    if (auth.currentUser && !auth.currentUser.emailVerified) {
      try {
        await sendEmailVerification(auth.currentUser);
        setError('');
      } catch (err) {
        setError(getReadableError(err));
        throw err;
      }
    }
  }

  // Login with Google
  async function loginWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setError('');
      return result.user;
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }

  // Logout
  async function logout() {
    try {
      await signOut(auth);
      setError('');
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }

  // Reset password
  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      setError('');
    } catch (err) {
      setError(getReadableError(err));
      throw err;
    }
  }

  const value = {
    currentUser,
    error,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword,
    resendVerificationEmail,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 